<template>
  <div class="container">
    <div class="tours-search-form-block bg-highlighted">
      <div class="field">
        <label class="label" for="tours-search-block-date">{{
          $texts('availableDates', 'Available dates')
        }}</label>
        <div class="select is-fullwidth">
          <select v-model="date" name="tours-search-block-date">
            <option
              v-for="option in dateOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="field">
        <label class="label" for="tours-search-block-date">{{
          $texts('numberOfGuests', 'Number of guests')
        }}</label>
        <div class="select is-fullwidth">
          <select v-model="people" name="tours-search-block-adults">
            <option
              v-for="option in peopleOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="field">
        <div class="control">
          <component
            :is="targetUrl ? 'a' : 'div'"
            :href="targetUrl"
            target="_blank"
            class="button is-primary"
            :disabled="!targetUrl ? true : null"
          >
            {{ $texts('search', 'Search') }}
          </component>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getDateParts } from '~/helpers'
const { $texts } = useEasyTexts()

defineBlokkliFragment({
  name: 'tours_search_form',
  label: 'Tours Search Form',
  chunkName: 'rare',
  editor: {
    noPreview: true,
  },
  globalOptions: ['bkHiddenGlobally', 'publicationChannels'],
})

type SearchFormBlockDateRange = {
  from: string
  to: string
}

const ENABLED_DATES: SearchFormBlockDateRange[] = [
  {
    from: '2025-01-04',
    to: '2025-01-05',
  },
  { from: '2025-01-11', to: '2025-01-12' },
]

const date = ref('')
const people = ref('1')
const peopleOptions = ref([
  {
    value: '1',
    label: '1',
  },
  {
    value: '2',
    label: '2',
  },
])

const dateOptions = computed(() =>
  ENABLED_DATES.map((item) => {
    return {
      value: item.from,
      label: formatDateRange(item),
    }
  }),
)

const locale = useCurrentLangcodeEnum()
const targetLanguage = computed(() => {
  const language = useCurrentLanguage()
  return language.value === 'de' ? 'de' : language.value
})

const targetUrl = computed(() => {
  const dateParts = getDateParts(date.value)
  if (dateParts.d && dateParts.m && dateParts.y) {
    const from = getDateKey(dateParts.d, dateParts.m, dateParts.y)
    const to = getDateKey(dateParts.d + 1, dateParts.m, dateParts.y)
    return `https://tours.zuerich.com/ZurichEvent2/ukv/?doSearch=1&date_from=${from}&date_to=${to}&number_adult[]=${people.value}&waiter=1&globalReset=1&lang=${targetLanguage.value}`
  }
  return ''
})

function formatDateRange(range: SearchFormBlockDateRange): string {
  const from = new Date(range.from)
  const to = new Date(range.to)
  return [formatDate(from, false), formatDate(to, true)].join(' – ')
}

function formatDate(date: Date, hasYear = false): string {
  return date.toLocaleDateString(locale.value, {
    weekday: 'short',
    year: hasYear ? 'numeric' : undefined,
    month: 'long',
    day: 'numeric',
  })
}

function getDateKey(d: number, m: number, y: number): string {
  return [
    d.toString().padStart(2, '0'),
    m.toString().padStart(2, '0'),
    y.toString(),
  ].join('.')
}
</script>

<style lang="postcss">
.tours-search-form-block {
  @apply grid items-end border border-solid border-border p-[1rem];
  grid-gap: 1rem;
  grid-template-columns: 1fr 1fr;
  @screen belowTablet {
    .field:first-child {
      grid-column: span 2;
    }
  }
  @screen tablet {
    grid-template-columns: 2fr 1fr auto;
    grid-gap: 1.5rem;
  }
  .button {
    @apply belowTablet:w-full;
  }
  .field {
    @apply mb-0;
  }
}
</style>
